/**
 * How a contact's loan entry is presented on the Borrowers & Leads contact page.
 *
 * Entries linked to the historical book (PFN-numbered loans) are shown as plain
 * past-loan history: no loan number, no Encompass markers, and editable like an
 * entry the user typed in. This is presentation only; the stored row is not
 * changed by anything in this module.
 *
 * The number test mirrors the loan-number half of `historicalMatch` in
 * supabase/functions/_shared/notification-loan-guard.ts. The two are kept in
 * lockstep by contact-loan-display-parity.test.ts. A historical loan whose only
 * signal is its folder cannot be recognised from a contact loan row, because the
 * row stores the number and not the folder.
 */
import type { ContactLoan } from './contactLoanService';

export type ContactLoanKind = 'pre_qual' | 'encompass' | 'past_loan';

export interface ContactLoanDisplay {
  kind: ContactLoanKind;
  /** Purpose text used in the entry title. */
  title: string;
  /** Value for the purpose select; a selectable option when one applies. */
  purposeValue: string;
  /** Text of the type badge for entries that are not live Encompass links. */
  badgeLabel: string;
  /** Status line shown under the badge. */
  statusLabel: string;
  /** Loan number to show, or null when it must not be displayed. */
  loanNumber: string | null;
  /** Whether purpose, property type and amounts can be edited. */
  editable: boolean;
  /** Whether the Encompass identifier block is shown. */
  showEncompassInfo: boolean;
}

type DisplayInput = Pick<
  ContactLoan,
  'loanType' | 'loanPurpose' | 'loanNumber' | 'status' | 'encompassLoanId'
>;

const CLOSED_STATUSES = new Set(['purchased', 'completion', 'completed', 'funded']);

/** True when a loan number belongs to the historical (PFN-numbered) book. */
export function isHistoricalLoanNumber(loanNumber?: string | null): boolean {
  return (loanNumber ?? '').trim().toUpperCase().startsWith('PFN');
}

function pastLoanPurpose(purpose?: string | null): string {
  const value = (purpose ?? '').trim();
  return value.toLowerCase() === 'nocash-out refinance' ? 'Refinance' : value;
}

function pastLoanStatus(status?: string | null): string {
  const value = (status ?? '').trim();
  return CLOSED_STATUSES.has(value.toLowerCase()) ? 'Closed' : value;
}

export function describeContactLoan(loan: DisplayInput): ContactLoanDisplay {
  const historical = isHistoricalLoanNumber(loan.loanNumber);

  if (loan.loanType === 'encompass' && historical) {
    const purpose = pastLoanPurpose(loan.loanPurpose);
    return {
      kind: 'past_loan',
      title: purpose || 'Past Loan',
      purposeValue: purpose,
      badgeLabel: 'Past Loan',
      statusLabel: pastLoanStatus(loan.status),
      loanNumber: null,
      editable: true,
      showEncompassInfo: false
    };
  }

  const isEncompass = loan.loanType === 'encompass';
  return {
    kind: isEncompass ? 'encompass' : 'pre_qual',
    title: loan.loanPurpose || 'Pre-Qualification',
    purposeValue: loan.loanPurpose || '',
    badgeLabel: 'Pre-Qual',
    statusLabel: loan.status || 'New',
    // A historical number is never displayed, whatever the entry type.
    loanNumber: historical ? null : loan.loanNumber || null,
    editable: !isEncompass,
    showEncompassInfo: Boolean(loan.encompassLoanId) && !historical
  };
}
